/**
 * MECE block reasons for policy / W2 termination flows.
 * String values must stay aligned with Prisma `BlockCategory` in apps/traba-server-node.
 */
export enum BlockCategory {
  ATTENDANCE = 'ATTENDANCE',
  SAFETY = 'SAFETY',
  CONDUCT_1 = 'CONDUCT_1',
  CONDUCT_2 = 'CONDUCT_2',
  PERFORMANCE = 'PERFORMANCE',
  OTHER = 'OTHER',
}

/**
 * Minimum note length for block / scheduled-block policy flows (API + ops-console).
 * TODO: Set back to 200 once AI IR + field population can draft longer narratives for users.
 */
export const MIN_BLOCK_POLICY_NOTE_LENGTH = 80

export const BLOCK_CATEGORY_LABEL: Record<BlockCategory, string> = {
  [BlockCategory.ATTENDANCE]: 'Attendance',
  [BlockCategory.SAFETY]: 'Safety',
  [BlockCategory.CONDUCT_1]: 'Conduct (Behavioral)',
  [BlockCategory.CONDUCT_2]: 'Conduct (Severe)',
  [BlockCategory.PERFORMANCE]: 'Performance',
  [BlockCategory.OTHER]: 'Other',
}

/** Shown in ops-console block-reason group headers (Figma: category rows use emoji + label). */
export const BLOCK_CATEGORY_EMOJI: Record<BlockCategory, string> = {
  [BlockCategory.ATTENDANCE]: '⏰',
  [BlockCategory.SAFETY]: '⚠️',
  [BlockCategory.CONDUCT_1]: '🚫',
  [BlockCategory.CONDUCT_2]: '⛔',
  [BlockCategory.PERFORMANCE]: '💪',
  [BlockCategory.OTHER]: '💬',
}

export const BLOCK_CATEGORY_ORDER: readonly BlockCategory[] = [
  BlockCategory.ATTENDANCE,
  BlockCategory.SAFETY,
  BlockCategory.CONDUCT_1,
  BlockCategory.CONDUCT_2,
  BlockCategory.PERFORMANCE,
  BlockCategory.OTHER,
]

/** Ops block-policy reason dropdown row order (groups SAFETY before conduct). */
export const BLOCK_POLICY_REASON_OPS_DROPDOWN_ORDER: readonly BlockCategory[] =
  [
    BlockCategory.SAFETY,
    BlockCategory.CONDUCT_2,
    BlockCategory.CONDUCT_1,
    BlockCategory.ATTENDANCE,
    BlockCategory.PERFORMANCE,
    BlockCategory.OTHER,
  ]

/** Keys are persisted `connectionReason` / `connectionReasons` string values. */
export const BLOCK_CONNECTION_REASON_TO_CATEGORY = {
  NO_CALL_NO_SHOW: BlockCategory.ATTENDANCE,
  CONSISTENTLY_ABSENT: BlockCategory.ATTENDANCE,
  CONSISTENTLY_LATE: BlockCategory.ATTENDANCE,
  SAFETY_VIOLATION: BlockCategory.SAFETY,
  INTOXICATION_OR_IMPAIRMENT: BlockCategory.SAFETY,
  INSUBORDINATION: BlockCategory.CONDUCT_1,
  UNPROFESSIONAL_OR_DISRUPTIVE: BlockCategory.CONDUCT_1,
  ABANDONED_SHIFT: BlockCategory.CONDUCT_1,
  VIOLENCE_THREATS_HARASSMENT: BlockCategory.CONDUCT_2,
  THEFT_OR_PROPERTY_DAMAGE: BlockCategory.CONDUCT_2,
  TIME_THEFT: BlockCategory.CONDUCT_2,
  EXCESSIVE_BREAKS: BlockCategory.PERFORMANCE,
  CONSTANT_BAD_PERFORMANCE_PRODUCTIVITY: BlockCategory.PERFORMANCE,
  UNQUALIFIED_FOR_REQUIREMENTS: BlockCategory.PERFORMANCE,
  NOT_A_GOOD_FIT: BlockCategory.OTHER,
  OTHER: BlockCategory.OTHER,
} as const

export type BlockConnectionPolicyReason =
  keyof typeof BLOCK_CONNECTION_REASON_TO_CATEGORY

export const BLOCK_POLICY_REASON_LABEL: Record<
  BlockConnectionPolicyReason,
  string
> = {
  NO_CALL_NO_SHOW: 'No call / no show',
  CONSISTENTLY_ABSENT: 'Consistently absent',
  CONSISTENTLY_LATE: 'Consistently late',
  SAFETY_VIOLATION: 'Safety violation',
  INTOXICATION_OR_IMPAIRMENT: 'Intoxication or impairment',
  INSUBORDINATION: 'Repeated insubordination',
  UNPROFESSIONAL_OR_DISRUPTIVE: 'Unprofessional or disruptive',
  ABANDONED_SHIFT: 'Abandoned shift',
  VIOLENCE_THREATS_HARASSMENT: 'Violence, threats, or harassment',
  THEFT_OR_PROPERTY_DAMAGE: 'Theft or property damage',
  TIME_THEFT: 'Time theft',
  EXCESSIVE_BREAKS: 'Excessive breaks',
  CONSTANT_BAD_PERFORMANCE_PRODUCTIVITY: 'Constant poor performance',
  UNQUALIFIED_FOR_REQUIREMENTS: 'Unqualified for job requirements',
  NOT_A_GOOD_FIT: 'Not a good fit',
  OTHER: 'Other',
}

const buckets: Record<BlockCategory, BlockConnectionPolicyReason[]> = {
  [BlockCategory.ATTENDANCE]: [],
  [BlockCategory.SAFETY]: [],
  [BlockCategory.CONDUCT_1]: [],
  [BlockCategory.CONDUCT_2]: [],
  [BlockCategory.PERFORMANCE]: [],
  [BlockCategory.OTHER]: [],
}

for (const reason of Object.keys(
  BLOCK_CONNECTION_REASON_TO_CATEGORY,
) as BlockConnectionPolicyReason[]) {
  buckets[BLOCK_CONNECTION_REASON_TO_CATEGORY[reason]].push(reason)
}

export const BLOCK_CATEGORY_TO_POLICY_REASONS = buckets as Readonly<
  Record<BlockCategory, readonly BlockConnectionPolicyReason[]>
>
